import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon, Popover2 } from '@evertrue/et-components';
import ExitConditionCard from './exit-condition-card';

/** The 4 condition types available in the type picker. */
const CONDITION_TYPE_OPTIONS = [
  { label: 'Portfolio stage reached', value: 'portfolio_stage_reached' },
  { label: 'Qualifies for a 2.0 Cadence', value: 'cadence_qualification' },
  { label: 'Qualifies for a saved search', value: 'saved_search' },
  { label: 'Added to a list', value: 'list_membership' },
];

/**
 * Exit Conditions section — wraps a list of ExitConditionCard components
 * with an "Add" button, type picker, and "always enforced" info popover.
 */
const ExitConditionsSection = ({
  conditions,
  onChange,
  portfolioTeams = {},
  cadences = [],
  savedSearches = [],
  contactLists = [],
}) => {
  const [showTypePicker, setShowTypePicker] = useState(false);
  const pickerRef = useRef(null);

  // Close type picker on outside click
  useEffect(() => {
    if (!showTypePicker) return;
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowTypePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showTypePicker]);

  const handleAddCondition = (type) => {
    const newCondition = {
      id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type,
      config: {},
    };
    onChange([...conditions, newCondition]);
    setShowTypePicker(false);
  };

  const handleRemoveCondition = (index) => {
    onChange(conditions.filter((_, i) => i !== index));
  };

  const handleChangeCondition = (index, updatedCondition) => {
    const updated = conditions.map((c, i) => (i === index ? updatedCondition : c));
    onChange(updated);
  };

  return (
    <div className="exit-conditions-section">
      <div className="exit-conditions-section--header">
        <div className="exit-conditions-section--header-right">
          <Popover2
            trigger={
              <button type="button" className="exit-conditions-section--info-link">
                Some exits are always enforced <Icon icon="chevron-right" />
              </button>
            }
            contentClassName="exit-conditions-section--enforced-popover"
          >
            {() => (
              <div className="exit-conditions-section--enforced-content">
                <strong>Always enforced</strong>
                <p>Constituents are automatically removed if:</p>
                <ul>
                  <li>The constituent has unsubscribed from outreach</li>
                  <li>Outreach bounces and no alternative contact info is available</li>
                  <li>The constituent is missing required info for the next step (e.g. no email or phone)</li>
                </ul>
              </div>
            )}
          </Popover2>
        </div>
      </div>
      <p className="exit-conditions-section--description">
        Control when constituents are automatically removed from this cadence.
      </p>

      <div className="exit-conditions-section--cards">
        {conditions.map((condition, index) => (
          <ExitConditionCard
            key={condition.id}
            condition={condition}
            onChange={(updated) => handleChangeCondition(index, updated)}
            onRemove={() => handleRemoveCondition(index)}
            portfolioTeams={portfolioTeams}
            cadences={cadences}
            savedSearches={savedSearches}
            contactLists={contactLists}
          />
        ))}
      </div>

      <div className="exit-conditions-section--add-wrapper" ref={pickerRef}>
        <button
          type="button"
          className="exit-conditions-section--add-btn"
          onClick={() => setShowTypePicker(!showTypePicker)}
        >
          <Icon icon="add" /> Add a removal condition
        </button>
        {showTypePicker && (
          <div className="exit-conditions-section--type-picker">
            {CONDITION_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="exit-conditions-section--type-option"
                onClick={() => handleAddCondition(opt.value)}
              >
                {opt.label}
              </button>
            ))}
            <button type="button" className="exit-conditions-section--cancel" onClick={() => setShowTypePicker(false)}>
              Cancel
            </button>
          </div>
        )}
      </div>

      <p className="exit-conditions-section--footer-note">
        <strong>Manual removal</strong> is always available to cadence managers from the cadence contact table.
      </p>
    </div>
  );
};

ExitConditionsSection.propTypes = {
  conditions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  portfolioTeams: PropTypes.object,
  cadences: PropTypes.array,
  savedSearches: PropTypes.array,
  contactLists: PropTypes.array,
};

export default ExitConditionsSection;
